import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AdminSurface from '../../../../components/admin/AdminSurface';
import type { GameMetadataMismatch } from '../../../../types/features/games';
import { adminCombineSx, adminEmptyStateSx, adminTableSx } from '../../../../styles/adminComponentStyles';

interface GameMetadataMismatchTableProps {
  games: GameMetadataMismatch[];
  loading: boolean;
}

const mismatchCellSx = (mismatch: boolean) => (
  mismatch ? { bgcolor: 'error.50', color: 'error.dark', fontWeight: 700 } : undefined
);

const GameMetadataMismatchTable: React.FC<GameMetadataMismatchTableProps> = ({ games, loading }) => (
  <AdminSurface sx={{ boxShadow: '0 2px 8px rgb(34 43 69 / 8%)', overflow: 'hidden' }}>
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table
        size="small"
        aria-label="game metadata mismatches table"
        sx={adminCombineSx(adminTableSx, {
          minWidth: 1120,
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': {
            padding: '8px 12px !important',
          },
          '& .MuiTableBody-root .MuiTableRow-root': {
            height: 42,
          },
          '& .MuiTableCell-body': {
            py: 0.75,
          },
        })}
      >
        <TableHead sx={{ '& th': { padding: '8px 12px !important', textTransform: 'uppercase' } }}>
          <TableRow>
            <TableCell>State</TableCell>
            <TableCell>Game</TableCell>
            <TableCell>Configured pack size</TableCell>
            <TableCell>Lottery pack size</TableCell>
            <TableCell>Configured ticket price</TableCell>
            <TableCell>Lottery ticket price</TableCell>
            <TableCell>Stores / packs</TableCell>
            <TableCell>Mismatch</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} sx={{ border: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress size={28} />
                </Box>
              </TableCell>
            </TableRow>
          ) : games.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} sx={{ border: 0, p: 1.5 }}>
                <Box sx={adminCombineSx(adminEmptyStateSx, { border: 0, minHeight: 96 })}>
                  <Typography className="AdminEmptyState-title">No metadata mismatches</Typography>
                  <Typography className="AdminEmptyState-copy">
                    Current lottery pack quantities and prices match the games table.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : games.map((game) => {
            const storeNames = game.stores.map((store) => store.name).join(', ');
            const mismatchLabels = [
              game.mismatches.tickets_per_pack ? 'Pack size' : null,
              game.mismatches.price_point ? 'Ticket price' : null,
            ].filter(Boolean).join(' + ');
            return (
              <TableRow key={`${game.state}-${game.game_id}`} hover>
                <TableCell>{game.state}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{game.game_id}</Typography>
                  {game.game_name && <Typography variant="caption" color="text.secondary">{game.game_name}</Typography>}
                </TableCell>
                <TableCell sx={mismatchCellSx(game.mismatches.tickets_per_pack)}>
                  {game.configured.tickets_per_pack ?? '—'}
                </TableCell>
                <TableCell sx={mismatchCellSx(game.mismatches.tickets_per_pack)}>
                  <Tooltip title={`Non-return quantities seen: ${game.observed.tickets_per_pack_values.join(', ')}`} arrow>
                    <span>{game.observed.tickets_per_pack ?? '—'}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={mismatchCellSx(game.mismatches.price_point)}>
                  {game.configured.price_point ? `$${game.configured.price_point}` : '—'}
                </TableCell>
                <TableCell sx={mismatchCellSx(game.mismatches.price_point)}>
                  <Tooltip title={`Ticket prices seen: ${game.observed.price_point_values.map((value) => `$${value}`).join(', ')}`} arrow>
                    <span>{game.observed.price_point ? `$${game.observed.price_point}` : '—'}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={storeNames || 'No stores'} arrow>
                    <span>{game.store_count} / {game.pack_count}</span>
                  </Tooltip>
                </TableCell>
                <TableCell><Chip label={mismatchLabels} size="small" color="error" /></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </AdminSurface>
);

export default React.memo(GameMetadataMismatchTable);
